import {
  PhysicalCardStatus,
  PhysicalCardStatusReason,
} from 'src/models/creditCard.models'

// Card details a card is missing before physical-card enrollment can proceed.
// Generic (not issuer-specific) — see extend-api's MissingCardDetail. EX-40639.
export enum MissingCardDetail {
  CardNumber = 'CARD_NUMBER',
  Expiry = 'EXPIRY',
  Cvc = 'CVC',
}

/**
 * A physical card whose enrollment failed for a reason the user can recover from by re-confirming
 * their card details: NOT_APPLICABLE with a recoverable reason (a bad CVC → CvsVerificationFailed,
 * or a generic UnknownFailure). ISSUER_NOT_SUPPORTED is terminal, and a null reason can't be told
 * apart from a terminal cause, so both are excluded — mirrors extend-api's
 * `PhysicalCardEnrollmentEligibilityService.isRecoverableFailure` (EX-40694).
 *
 * This is only half of the guard: every caller must ALSO require non-empty `missingCardDetails`
 * before routing into the confirm flow. That non-emptiness encodes both "flag on" and "backend
 * deployed" — an old backend returns an empty set for these accounts, so the surfaces stay hidden,
 * byte-identical to today (see the deploy-order safety note in EX-40694).
 */
export const isRecoverableEnrollmentFailure = (physicalCard: {
  status?: PhysicalCardStatus | null
  statusReason?: PhysicalCardStatusReason | null
}): boolean =>
  physicalCard.status === PhysicalCardStatus.NotApplicable &&
  (physicalCard.statusReason ===
    PhysicalCardStatusReason.CvsVerificationFailed ||
    physicalCard.statusReason === PhysicalCardStatusReason.UnknownFailure)

/**
 * Whether AccountSettings should replace the whole page with the static CVS-failed blocked box.
 * Extracted so both arms are testable without a full-page render (EX-40694): the suppression is a
 * composition of `isRecoverableEnrollmentFailure` AND confirmable missing details, and testing only
 * the leaf predicate leaves the composition — the actual takeover decision — uncovered.
 *
 * `hasConfirmableMissingDetails` is the caller's `missingCardDetails.length > 0 && canConnect`,
 * which is false with the flag off or against a backend that doesn't populate `missingCardDetails`
 * — so the takeover stays byte-identical to pre-EX-40694 in both of those cases.
 */
export const shouldShowCvsVerificationFailedTakeover = (params: {
  isBlocked?: boolean
  physicalCard: {
    status?: PhysicalCardStatus | null
    statusReason?: PhysicalCardStatusReason | null
  }
  hasConfirmableMissingDetails: boolean
}): boolean =>
  !params.isBlocked &&
  !(
    isRecoverableEnrollmentFailure(params.physicalCard) &&
    params.hasConfirmableMissingDetails
  ) &&
  params.physicalCard.status === PhysicalCardStatus.NotApplicable &&
  params.physicalCard.statusReason ===
    PhysicalCardStatusReason.CvsVerificationFailed

/** Physical-card statuses that mean enrollment/confirmation is done — no further 3DS/CVS step needed. */
export const isCompletePhysicalCardStatus = (
  status?: PhysicalCardStatus | null,
): boolean =>
  status === PhysicalCardStatus.Active ||
  status === PhysicalCardStatus.NeedsCVSVerification
